import React, { useCallback, useEffect, useRef, useState } from "react";

import styles from "./SidebarResizeHandle.module.css";

/**
 * A thin draggable handle on the left edge of a right-anchored sidebar.
 * Dragging left widens the sidebar, dragging right shrinks it (the caller's
 * `onResize` clamps to the min/max). Because the sidebar is anchored to the
 * right of the viewport, the new width is `window.innerWidth - clientX`.
 */
function SidebarResizeHandle({ onResize }) {
  const [dragging, setDragging] = useState(false);
  const onResizeRef = useRef(onResize);

  useEffect(() => {
    onResizeRef.current = onResize;
  }, [onResize]);

  const handleMouseDown = useCallback((event) => {
    event.preventDefault();
    setDragging(true);
  }, []);

  useEffect(() => {
    if (!dragging) {
      return undefined;
    }

    const handleMove = (event) => {
      onResizeRef.current?.(window.innerWidth - event.clientX);
    };

    const handleUp = () => {
      setDragging(false);
    };

    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "ew-resize";

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
    };
  }, [dragging]);

  return (
    <div
      className={`${styles.handle} ${dragging ? styles.dragging : ""}`}
      onMouseDown={handleMouseDown}
      role="separator"
      aria-orientation="vertical"
    />
  );
}

export default SidebarResizeHandle;
